// src/components/settings/NotificationSettings.jsx
// Add this component to your Settings page

import React from 'react';
import { Bell, BellOff, Smartphone, Globe, CheckCircle, XCircle } from 'lucide-react';
import { usePushNotifications } from '../../hooks/usePushNotifications';

const NotificationSettings = ({ user, currentGroup }) => {
  const {
    permission,
    supported,
    loading,
    error,
    platform,
    isEnabled,
    canRequest,
    requestPermission,
  } = usePushNotifications(user, currentGroup);

  const handleEnable = async () => {
    const granted = await requestPermission();
    if (!granted && permission === 'denied') {
      // Permission was denied - show instructions
      alert(
        platform === 'web'
          ? 'Notifications are blocked. Please enable them in your browser settings.'
          : 'Notifications are blocked. Please enable them in your device settings.'
      );
    }
  };

  // Not supported on this device/browser
  if (!supported) {
    return (
      <div className="bg-gray-50 dark:bg-gray-900 rounded-xl p-4">
        <div className="flex items-center gap-3">
          <BellOff className="text-gray-400" size={24} />
          <div>
            <h3 className="font-semibold text-gray-700 dark:text-gray-300">
              Push Notifications
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Not supported on this {platform === 'web' ? 'browser' : 'device'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 dark:bg-gray-900 rounded-xl p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {isEnabled ? (
            <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
              <Bell className="text-green-600 dark:text-green-400" size={24} />
            </div>
          ) : (
            <div className="p-2 bg-gray-100 dark:bg-gray-800 rounded-lg">
              <BellOff className="text-gray-400" size={24} />
            </div>
          )}
          <div>
            <h3 className="font-semibold text-gray-800 dark:text-gray-100">
              Push Notifications
            </h3>
            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
              {platform === 'web' ? (
                <Globe size={14} />
              ) : (
                <Smartphone size={14} />
              )}
              <span className="capitalize">{platform}</span>
              {isEnabled && (
                <>
                  <span>•</span>
                  <CheckCircle size={14} className="text-green-500" />
                  <span className="text-green-600 dark:text-green-400">Active</span>
                </>
              )}
            </div>
          </div>
        </div>

        {isEnabled ? (
          <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
            <CheckCircle size={20} />
            <span className="text-sm font-medium">Enabled</span>
          </div>
        ) : canRequest ? (
          <button
            onClick={handleEnable}
            disabled={loading}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? 'Enabling...' : 'Enable'}
          </button>
        ) : (
          <div className="flex items-center gap-2 text-red-500">
            <XCircle size={20} />
            <span className="text-sm font-medium">Blocked</span>
          </div>
        )}
      </div>

      {/* Error message */}
      {error && (
        <p className="mt-3 text-sm text-red-500 dark:text-red-400">
          Error: {error}
        </p>
      )}

      {/* Permission denied help */}
      {permission === 'denied' && (
        <div className="mt-3 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
          <p className="text-sm text-yellow-800 dark:text-yellow-200">
            <strong>Notifications are blocked.</strong> To enable them:
          </p>
          {platform === 'web' ? (
            <ol className="mt-2 text-sm text-yellow-700 dark:text-yellow-300 list-decimal list-inside space-y-1">
              <li>Click the lock/info icon in your browser's address bar</li>
              <li>Find "Notifications" and change to "Allow"</li>
              <li>Refresh the page</li>
            </ol>
          ) : (
            <ol className="mt-2 text-sm text-yellow-700 dark:text-yellow-300 list-decimal list-inside space-y-1">
              <li>Open your device Settings</li>
              <li>Find Boply in the app list</li>
              <li>Enable Notifications</li>
            </ol>
          )}
        </div>
      )}

      {/* What you'll get */}
      {isEnabled && (
        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            You'll be notified when:
          </p>
          <ul className="text-sm text-gray-500 dark:text-gray-400 space-y-1">
            <li>🎸 A band member adds a new show</li>
            <li>📅 A band member adds a new event</li>
            <li>🗳️ Someone creates a poll</li>
            <li>⏰ You have a show today or tomorrow</li>
          </ul>
        </div>
      )}
    </div>
  );
};

export default NotificationSettings;